// /web/src/pages/Terms.jsx
const lastUpdated = new Date().toISOString().slice(0, 10);

export default function Terms() {
  return (
    <section className="pd-blog prose">
      <header className="pd-blog__header">
        <p className="small" style={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
          Last updated {lastUpdated}
        </p>
        <h1>Terms of Service</h1>
        <p className="lead" style={{ marginTop: 0 }}>
          Legal terms covering accounts, usage, and service availability for Pebbledash customers.
        </p>
      </header>

      <article className="pd-blog__body">
        <h2>1. About Pebbledash</h2>
        <p>
          Pebbledash is a SaaS platform that provides early-stage design calculators (e.g., lifts,
          WCs, stairs, cycles). By creating an account or using the service you agree to these
          Terms.
        </p>

        <h2>2. Accounts</h2>
        <p>
          You are responsible for maintaining the confidentiality of your account and for any
          activity under it. Do not share your credentials. You must promptly notify us of any
          unauthorised use.
        </p>

        <h2>3. Usage &amp; tokens</h2>
        <p>
          Plans include a monthly token allowance. Each calculator run consumes tokens as shown in
          the UI. We may investigate abnormal usage and suspend accounts that abuse the service.
        </p>

        <h2>4. Billing</h2>
        <p>
          Subscriptions are processed by Stripe. Taxes may apply. You can cancel anytime; access
          continues until the end of the paid period. Refunds are handled according to your
          statutory rights and our Refunds section on the Pricing page.
        </p>

        <h2>5. Your content</h2>
        <p>
          You retain ownership of your project data. You grant Pebbledash a limited license to
          process it to provide and improve the service (including quality assurance, support, and
          security). You represent that you have the right to input such data.
        </p>

        <h2>6. Outputs &amp; accuracy</h2>
        <p>
          Outputs are for guidance only and rely on assumptions you control. They do not
          constitute professional advice or compliance certification. Always verify against current
          codes, standards, and local policies.
        </p>

        <h2>7. Acceptable use</h2>
        <p>
          Don’t attempt to reverse engineer, overload, or misuse the service. Don’t use it to
          violate law or infringe others’ rights.
        </p>

        <h2>8. IP</h2>
        <p>
          The Pebbledash platform, brand, and code are protected by intellectual property laws. No
          rights are granted except as needed to use the service.
        </p>

        <h2>9. Warranties &amp; liability</h2>
        <p>
          The service is provided “as is.” To the maximum extent permitted by law, we exclude all
          implied warranties and limit liability to fees paid in the preceding 12 months. Nothing
          limits liability for fraud or where not permitted by law.
        </p>

        <h2>10. Changes</h2>
        <p>
          We may update these Terms; material changes will be notified in-app or by email.
          Continued use after an update constitutes acceptance.
        </p>

        <h2>11. Contact</h2>
        <p>
          <a href="mailto:hello@pebbledash.co">hello@pebbledash.co</a>
        </p>

        {/* --- New risk & liability clauses --- */}

        <h2>12. Professional responsibility disclaimer</h2>
        <p>
          Pebbledash is <strong>not</strong> a substitute for professional architectural,
          structural, fire, MEP, or building control services. Our calculators generate indicative
          metrics only. You are solely responsible for review by suitably qualified professionals
          and for ensuring compliance with applicable law, codes, guidance, and local authority
          requirements.
        </p>

        <h2>13. Regulatory changes &amp; assumptions</h2>
        <ul>
          <li>
            Codes, standards, guidance and local planning policies change. Pebbledash does not
            promise real-time updates or automatic conformance with any given edition or local
            interpretation.
          </li>
          <li>
            You must confirm (a) the correct standard/policy applies, (b) any project-specific
            exemptions, and (c) that all inputs, diversity/occupancy ratios, and selection options
            are appropriate for your scheme.
          </li>
        </ul>

        <h2>14. Beta features, estimates &amp; limitations</h2>
        <p>
          Some calculators and features may be labelled Beta or Experimental. Estimates (e.g.,
          counts, sizes, or performance metrics) are approximate and depend on your inputs,
          underlying datasets, and model constraints. Do not use results as the sole basis for
          safety-critical, procurement, or statutory decisions.
        </p>

        <h2>15. Service availability &amp; data</h2>
        <ul>
          <li>We do not guarantee the service will be uninterrupted, timely, secure, or error-free.</li>
          <li>
            You are responsible for exporting/backing up your data and results. We have no
            obligation to retain your data beyond what is necessary to provide the service.
          </li>
          <li>Planned or emergency maintenance may occur without notice.</li>
        </ul>

        <h2>16. Third-party services</h2>
        <p>
          Pebbledash may integrate with third-party services (e.g., Auth0, Stripe, postcode or
          policy APIs). Their availability, accuracy, and security are outside our control, and your
          use is governed by their terms. We are not liable for loss arising from third-party
          failures.
        </p>

        <h2>17. Expanded limitation of liability</h2>
        <p>
          To the fullest extent permitted by law, Pebbledash and its officers, employees, and
          contributors shall not be liable for any indirect, consequential, exemplary, incidental,
          or special damages (including loss of profit, revenue, data, goodwill, or business
          interruption), whether arising in contract, tort (including negligence), or otherwise,
          even if advised of the possibility. Our total aggregate liability arising out of or
          relating to the service in any 12-month period shall not exceed the greater of (a) the
          amounts you paid to Pebbledash in that period, or (b) £100. Nothing in these Terms
          excludes liability where such exclusion is unlawful.
        </p>

        <h2>18. Indemnity</h2>
        <p>
          You agree to indemnify and hold harmless Pebbledash, its affiliates and personnel from and
          against any claims, liabilities, damages, losses, and expenses (including reasonable legal
          fees) arising out of or in any way connected with (a) your use or misuse of the service,
          (b) your breach of these Terms, or (c) reliance by any third party on your exported
          outputs.
        </p>

        <h2>19. Suspension &amp; termination</h2>
        <p>
          We may suspend or terminate access if you breach these Terms, fail to pay fees, or create
          risk to the service or other users. Upon termination, your right to use the service ends;
          the sections that by their nature should survive (including IP, liability limits,
          indemnity, and governing law) will continue to apply.
        </p>

        <h2>20. Governing law &amp; venue</h2>
        <p>
          These Terms and any non-contractual obligations arising out of or in connection with them
          are governed by the laws of England and Wales. The courts of England shall have exclusive
          jurisdiction.
        </p>

        <h2>21. Miscellaneous</h2>
        <ul>
          <li>
            <strong>Entire agreement.</strong> These Terms constitute the entire agreement between
            you and Pebbledash regarding the service.
          </li>
          <li>
            <strong>Severability.</strong> If any provision is found unenforceable, the remaining
            provisions remain in full force.
          </li>
          <li>
            <strong>No waiver.</strong> Failure to enforce any right is not a waiver of that right.
          </li>
          <li>
            <strong>Assignment.</strong> You may not assign these Terms without our consent.
          </li>
        </ul>
      </article>
    </section>
  );
}
